import React, { lazy, Suspense } from "react";
import { ChatMeeting } from "@fidget/common/components/chat/chatMeeting";
import { ChatItemGroup } from "@fidget/common/models/chat/chatItemGroup";
import {
  CallProposalChatItem,
  CobrowsingProposalChatItem,
  ContactEnquiryChatItem,
  DateSeparatorChatItem,
  DynamicSuggestionsChatItem,
  EmptyChatItem,
  IChatItem,
  IGroupableItem,
  MeetingChatItem,
  MeetingSchedulerChatItem,
  MessageWithFormChatItem,
  NoteChatItem,
  NotificationChatItem,
  StatusChangeChatItem,
  TypingChatItem,
  UserTextChatItem
} from "@fidget/common/models/chat/chatItems";
import {
  isAutoAssignConfigContext,
  isContactEnquiryConfigContext,
  isOplAddressConfigContext,
  isOrdinaryConfigContext,
  isPredefinedMessageConfigContext
} from "@fidget/common/models/shared/spinner/types/messageWidgets/WidgetFormSubmissionWithWidget";
import {
  equals,
  isAgentProfile,
  isBotProfile,
  isGuestProfile,
  UserProfile
} from "@fidget/common/models/userProfile";
import { logger } from "@fidget/common/utils";
import { ChatMeetingScheduler } from "./chatMeetingScheduler";
import { ChatMessage } from "./chatMessage";
import { ChatNotification } from "./chatNotification";
import { ChatSection } from "./chatSection";
import { ChatStatusNotification } from "./chatStatusNotification";
import { DateSeparator } from "./dateSeparator";
import { MessageWidgetAutoAssignForm } from "./messageWidgetForm/autoAssignForm/messageWidgetAutoAssignForm";
import { CenterPreloader } from "../centerPreloader/centerPreloader";
import { applyUrlParameter } from "@fidget/common/models/chat/utils";
import { UrlParameter } from "@closerplatform/spinner-openapi";
import { TypingIndicator } from "./typingIndicator";
import { DynamicSuggestions } from "./richMessages/components/dynamicSuggestions/dynamicSuggestions";

export type ContactProvidedCallback = (contact: string) => void;

const AsyncChatContactEnquiry = lazy(
  () => import("@fidget/common/components/chat/chatContactEnquiry")
);

export function renderItem(
  item: IChatItem,
  currentUser: UserProfile,
  phoneRegion: string,
  onContactProvided?: ContactProvidedCallback,
  scrollToBottom?: () => void,
  isMobile?: boolean
): React.ReactNode {
  const noContactInfoSaved = () =>
    !(isGuestProfile(currentUser) && (!!currentUser.email || !!currentUser.phone));

  if (ChatItemGroup.isChatItemGroup(item)) {
    return renderChatGroup(item, currentUser, scrollToBottom, onContactProvided, isMobile);
  }
  if (UserTextChatItem.isUserTextChatItem(item)) {
    return renderUserTextItem(item, currentUser, scrollToBottom, isMobile);
  }
  if (TypingChatItem.isTypingChatItem(item)) {
    return renderTypingChatItem(item);
  }
  if (DynamicSuggestionsChatItem.isDynamicSuggestionsChatItem(item)) {
    return renderDynamicSuggestionsChatItem(item, isMobile);
  }
  if (NotificationChatItem.isNotificationChatItem(item)) {
    return renderNotificationItem(item);
  }
  if (DateSeparatorChatItem.isDateSeparatorChatItem(item)) {
    return renderDateSeparatorItem(item);
  }
  if (NoteChatItem.isNoteChatItem(item)) {
    return renderNoteItem(item);
  }
  if (ContactEnquiryChatItem.isContactEnquiryChatItem(item) && noContactInfoSaved()) {
    return renderContactEnquiryItem(item, phoneRegion, onContactProvided);
  }
  if (MeetingChatItem.isMeetingChatItem(item)) {
    return renderMeetingItem(item);
  }
  if (MeetingSchedulerChatItem.isMeetingSchedulerChatItem(item)) {
    return renderMeetingSchedulerItem(item);
  }
  if (CallProposalChatItem.isCallProposalChatItem(item)) {
    return renderCallProposalItem(item, currentUser, scrollToBottom, isMobile);
  }
  if (CobrowsingProposalChatItem.isCobrowsingProposalChatItem(item)) {
    return renderCobrowsingProposalItem(item, currentUser, scrollToBottom, isMobile);
  }
  if (MessageWithFormChatItem.isMessageWithFormChatItem(item)) {
    return renderMessageWithFormItem(item, currentUser, scrollToBottom, isMobile);
  }
  if (StatusChangeChatItem.isStatusChangeChatItem(item)) {
    return renderStatusChangeItem(item, currentUser);
  }
  if (EmptyChatItem.isEmptyChatItem(item)) {
    return renderEmptyItem(item);
  }

  return renderFallbackItem(item);
}

/*
 Items' renders
*/

export function shouldAlignRightInWidget(user: UserProfile, currentUser: UserProfile): boolean {
  return equals(user, currentUser);
}
export function shouldAlignRightInDashboard(user: UserProfile): boolean {
  return isAgentProfile(user);
}

function renderFallbackItem(item: IChatItem): React.ReactNode {
  return <ChatNotification key={item.id} message={item.tag} />;
}

function renderEmptyItem(item: IChatItem): React.ReactNode {
  return <React.Fragment key={item.id} />;
}

function renderStatusChangeItem(
  item: StatusChangeChatItem,
  currentUser: UserProfile
): React.ReactNode {
  return <ChatStatusNotification {...item} key={item.id} currentUser={currentUser} />;
}

function renderMessageWithFormItem(
  item: MessageWithFormChatItem,
  currentUser: UserProfile,
  scrollToBottom?: () => void,
  isMobile?: boolean
): React.ReactNode {
  const alignRight = shouldAlignRightInWidget(item.user, currentUser);
  const { submission } = item;

  if (isPredefinedMessageConfigContext(submission.widgetConfig)) {
    const messageWithParams = applyUrlParameter(
      submission.widgetConfig.message,
      UrlParameter.RoomId,
      submission.roomId
    );

    return (
      <ChatMessage
        key={item.id}
        messageId={item.id}
        text={messageWithParams}
        alignRight={alignRight}
        fromAgent={isAgentProfile(item.user)}
        fromBot={isBotProfile(item.user)}
        scrollToBottom={scrollToBottom}
        isMobile={isMobile}
      />
    );
  }

  if (
    isOrdinaryConfigContext(submission.widgetConfig) ||
    isOplAddressConfigContext(submission.widgetConfig) ||
    isContactEnquiryConfigContext(submission.widgetConfig)
  ) {
    return (
      <ChatMessage
        key={item.id}
        messageId={item.id}
        text={item.text}
        alignRight={alignRight}
        fromAgent={isAgentProfile(item.user)}
        fromBot={isBotProfile(item.user)}
        submission={submission}
        scrollToBottom={scrollToBottom}
        isMobile={isMobile}
      />
    );
  }
  if (isAutoAssignConfigContext(submission.widgetConfig)) {
    return (
      <React.Fragment key={item.id}>
        <ChatMessage
          key={item.id}
          messageId={item.id}
          text={item.text}
          alignRight={alignRight}
          fromAgent={isAgentProfile(item.user)}
          fromBot={isBotProfile(item.user)}
          scrollToBottom={scrollToBottom}
          isMobile={isMobile}
        />
        <MessageWidgetAutoAssignForm
          scrollToBottom={scrollToBottom}
          messageId={item.id}
          submission={submission}
          submissionConfig={submission.widgetConfig}
        />
      </React.Fragment>
    );
  }

  logger.warn("Message with unknown form type", item);
  return;
}

function renderCobrowsingProposalItem(
  item: CobrowsingProposalChatItem,
  currentUser: UserProfile,
  scrollToBottom?: () => void,
  isMobile?: boolean
): React.ReactNode {
  const alignRight = shouldAlignRightInWidget(item.user, currentUser);
  return (
    <ChatMessage
      key={item.id}
      messageId={item.id}
      text={item.text}
      alignRight={alignRight}
      fromAgent={isAgentProfile(item.user)}
      fromBot={isBotProfile(item.user)}
      withCobrowsingDialog
      scrollToBottom={scrollToBottom}
      isMobile={isMobile}
    />
  );
}

function renderCallProposalItem(
  item: CallProposalChatItem,
  currentUser: UserProfile,
  scrollToBottom?: () => void,
  isMobile?: boolean
): React.ReactNode {
  const alignRight = shouldAlignRightInWidget(item.user, currentUser);
  return (
    <ChatMessage
      key={item.id}
      messageId={item.id}
      text={item.text}
      alignRight={alignRight}
      fromAgent={isAgentProfile(item.user)}
      fromBot={isBotProfile(item.user)}
      withCallButton
      scrollToBottom={scrollToBottom}
      isMobile={isMobile}
    />
  );
}

function renderMeetingSchedulerItem(item: MeetingSchedulerChatItem): React.ReactNode {
  return <ChatMeetingScheduler key={item.id} status={item.progressStatus} />;
}

function renderMeetingItem(item: MeetingChatItem): React.ReactNode {
  return (
    <ChatMeeting
      key={item.id}
      agent={item.agent}
      start={item.start}
      duration={item.duration}
      meetingStatus={item.meetingStatus}
      changed={item.changed}
      widgetStyle
    />
  );
}

function renderContactEnquiryItem(
  item: ContactEnquiryChatItem,
  phoneRegion: string,
  onSubmit?: ContactProvidedCallback
): React.ReactNode {
  const shouldShowConsent = item.gdprEnabled;

  return (
    <Suspense fallback={<CenterPreloader />}>
      <AsyncChatContactEnquiry
        key={item.id}
        messageId={item.id}
        message={item.text}
        onSubmit={onSubmit}
        showConsent={shouldShowConsent}
        phoneRegion={phoneRegion}
      />
    </Suspense>
  );
}

function renderNoteItem(item: NoteChatItem): React.ReactNode {
  return <React.Fragment key={item.id} />;
}

function renderDateSeparatorItem(item: DateSeparatorChatItem): React.ReactNode {
  return <DateSeparator key={"separator_" + item.timestamp} formattedDate={item.id} />;
}

function renderNotificationItem(item: NotificationChatItem): React.ReactNode {
  return <ChatNotification key={item.id} message={item.text} />;
}

function renderTypingChatItem(item: TypingChatItem): React.ReactNode {
  return <TypingIndicator key={item.id} />;
}

function renderDynamicSuggestionsChatItem(
  item: DynamicSuggestionsChatItem,
  isMobile?: boolean
): React.ReactNode {
  return (
    <DynamicSuggestions
      key={item.id}
      dynamicSugestions={item.dynamicSuggestions}
      isMobile={isMobile}
    />
  );
}

function renderUserTextItem(
  item: UserTextChatItem,
  currentUser: UserProfile,
  scrollToBottom?: () => void,
  isMobile?: boolean
): React.ReactNode {
  const alignRight = shouldAlignRightInWidget(item.user, currentUser);
  return (
    <ChatMessage
      key={item.id}
      text={item.text}
      messageId={item.id}
      context={item.context}
      attachments={item.attachments}
      alignRight={alignRight}
      fromAgent={isAgentProfile(item.user)}
      fromBot={isBotProfile(item.user)}
      scrollToBottom={scrollToBottom}
      isMobile={isMobile}
    />
  );
}

function renderChatGroup(
  item: ChatItemGroup<IGroupableItem>,
  currentUser: UserProfile,
  scrollToBottom?: () => void,
  onContactProvided?: ContactProvidedCallback,
  isMobile?: boolean
): React.ReactNode {
  return (
    <ChatSection
      key={item.id}
      item={item}
      currentUser={currentUser}
      onContactProvided={onContactProvided}
      scrollToBottom={scrollToBottom}
      isMobile={isMobile}
    />
  );
}
